import { type Avatar, DEFAULT_AVATAR, type CurrentUser } from "@talkers/shared";
import { createResource, createRoot } from "solid-js";
import { api } from "@/api/client";
import { settings } from "@/state/settings";

/**
 * 「いま誰か」をまとめる。
 *
 * 名乗っただけのゲストもサーバ側の `users` の行を持つ (better-auth の anonymous)。
 * なので名前と猫の正は常にサーバで、編集は誰であれ `PATCH /me`。
 *
 * セッションはページを開いた時点では作らない。読むだけの訪問者にまでユーザーを
 * 作らないよう、名乗るか書き込むかしたときに `ensureSession()` で作る。
 */
export const { me, refetchMe, config } = createRoot(() => {
  const [meResource, { refetch }] = createResource(async () => (await api.getMe()).data);
  const [configResource] = createResource(async () => (await api.getConfig()).data);
  return { me: meResource, refetchMe: refetch, config: configResource };
});

/** メールや Google で入っているか。ゲストは false。 */
export const isLoggedIn = (): boolean => me() != null && me()?.isAnonymous !== true;

/** admin の権限を持っているか。管理者モードの切り替え自体だけがこれを見る。 */
export const hasAdminRole = (): boolean => me()?.role === "admin";

/**
 * bot バッジや非表示ボタンのように、運用者にだけ見せたいものの出し分けに使う。
 * 管理者モードを切ると普通のユーザーと同じ見え方になる (権限は変わらない)。
 */
export const isAdmin = (): boolean => hasAdminRole() && settings().adminMode;

/** 表示に使う名前とアバター。まだセッションが無ければ空。 */
export function currentDisplay(): { name: string; avatar: Avatar } {
  const user = me();
  if (user != null) return { name: user.name, avatar: user.avatar };
  return { name: "", avatar: DEFAULT_AVATAR };
}

/** 名乗る・書き込む直前に呼ぶ。セッションが無ければゲストとして作る。 */
export async function ensureSession(): Promise<CurrentUser> {
  const current = me();
  if (current != null) return current;
  await api.signInAnonymous();
  const next = await refetchMe();
  if (next == null) throw new Error("ゲストとして始められませんでした");
  return next;
}

/** 名前とアバターを保存する。ゲストでも同じ経路。 */
export async function saveDisplay(next: { name: string; avatar: Avatar }): Promise<void> {
  await ensureSession();
  await api.updateMe({ name: next.name, avatar: next.avatar });
  await refetchMe();
}

/** ログイン直後などにサーバ側の状態を取り直す。 */
export async function reloadMe(): Promise<CurrentUser | null> {
  await refetchMe();
  return me() ?? null;
}
